import {
  DATA_STORED_IN_API_CACHE_SECS,
  DDM_SERVICE_PREFIX,
  DMS_API_PREFIX,
  MAX_RETRY_ATTEMPT,
  Method,
  RCMS_SERVICE_PREFIX,
  UMS_SERVICE_PREFIX,
} from './GlobalConstants';
import { metricsPublisher } from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import { PUBLIC_URI_PREFIX } from './UrlConstants';

export interface IAPIContants {
  getRoute(...params): string;
  getPublicRoute(...params): string;
  method: Method;
  metricsPublisher: KatalMetrics.Publisher;
  numberOfRetries: number;
  keepUnusedDataFor: number;
}

/**
 * RCMS API's for
 * 1. Create/View/List Case
 * 2. Add/List Correspondences
 */

export const GetListCases: IAPIContants = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/ListCases`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${GetListCases.getRoute()}`,
  method: Method.POST,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('ListCasesApi'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const CreateCase: IAPIContants = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/CreateCase`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${CreateCase.getRoute()}`,
  method: Method.POST,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('CreateCaseApi'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const AnonymousCreateCase: IAPIContants = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/CreateCase`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${AnonymousCreateCase.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'AnonymousCreateCaseApi',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const UpdateCase: IAPIContants = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/UpdateCase`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${UpdateCase.getRoute()}`,
  method: Method.POST,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('UpdateCaseApi'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const GetCaseDetails = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/ViewCase`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${GetCaseDetails.getRoute()}`,
  method: Method.POST,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('ViewCaseApi'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepDataUnusedFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const AddCorrespondence: IAPIContants = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/AddCorrespondence`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${AddCorrespondence.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'AddCorrespondenceApi',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const ListCorrespondence: IAPIContants = {
  getRoute: () => `${RCMS_SERVICE_PREFIX}/ListCorrespondence`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${ListCorrespondence.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'ListCorrespondenceApi',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

/**
 * DMS File Upload API's
 */

export const GET_UPLOAD_S3_URL: IAPIContants = {
  getRoute: () => `${DMS_API_PREFIX}/api/v1/uploads`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${GET_UPLOAD_S3_URL.getRoute()}`,
  method: Method.POST,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('UploadS3UrlApi'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const UPLOAD_DMS_DOCUMENT_TO_S3: IAPIContants = {
  getRoute: () => ``,
  getPublicRoute: () =>
    `${PUBLIC_URI_PREFIX}${UPLOAD_DMS_DOCUMENT_TO_S3.getRoute()}`,
  method: Method.PUT,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'UploadDMSDocumentToS3',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const CREATE_DOCUMENT_S3_URL: IAPIContants = {
  getRoute: () => `${DMS_API_PREFIX}/api/v1/partitions/LENS/documents`,
  getPublicRoute: () =>
    `${PUBLIC_URI_PREFIX}${CREATE_DOCUMENT_S3_URL.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'CreateDMSDocumentApi',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const DOWNLOAD_DMS_S3_DOCUMENT: IAPIContants = {
  getRoute: (documentId: string) =>
    `${DMS_API_PREFIX}/api/v1/partitions/LENS/documents/${documentId}/content?asAttachment=true`,
  getPublicRoute: () =>
    `${PUBLIC_URI_PREFIX}${DOWNLOAD_DMS_S3_DOCUMENT.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'DownloadDMSDocumentAPI',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

/**
 * UMS API's
 */

export const GetUserAuthorizedResources: IAPIContants = {
  getRoute: () => `${UMS_SERVICE_PREFIX}/GetUserAuthorizedResources`,
  getPublicRoute: () =>
    `${PUBLIC_URI_PREFIX}${GetUserAuthorizedResources.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'GetUserAuthorizedResources',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const AddUserToAccount: IAPIContants = {
  getRoute: () => `${UMS_SERVICE_PREFIX}/AddUserToAccount`,
  getPublicRoute: () => `${PUBLIC_URI_PREFIX}${AddUserToAccount.getRoute()}`,
  method: Method.POST,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('AddUserToAccount'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const RemoveUserFromAccount: IAPIContants = {
  getRoute: () => `${UMS_SERVICE_PREFIX}/RemoveUserFromAccount`,
  getPublicRoute: () =>
    `${PUBLIC_URI_PREFIX}${RemoveUserFromAccount.getRoute()}`,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'RemoveUserFromAccount',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

export const GetUsersFromAccount: IAPIContants = {
  getRoute: () => `${UMS_SERVICE_PREFIX}/ListUserDetailsForAnAccount`,
  getPublicRoute: () => ``,
  method: Method.POST,
  metricsPublisher: metricsPublisher.newChildActionPublisherForMethod(
    'ListUserDetailsForAnAccount',
  ),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};

/**
 * DDM API's
 */
export const GetDDMSchema: IAPIContants = {
  /**
   * Generates routes for GetDDMSchema
   * @see: https://w.amazon.com/bin/view/ConnectedComplianceTech/ComplianceManagement/Design/DDM/DDM_1_0/#HGetSchemaAPI
   * @param clientId 'CPR' is the clientId used for DDM
   * @param schemaKey Its the accountId for the tenant
   * @param schemaVersion It's the version of schema to be fetched. The value is LATEST_PUBLISHED by default and the number otherwise.
   * @returns Generated routes for GetSchema.
   */
  getRoute: (clientId: string, schemaKey: string, schemaVersion?: string) => {
    const route = `${DDM_SERVICE_PREFIX}/get-schema?clientId=${clientId}&schemaKey=${schemaKey}`;
    return !schemaVersion ? route : `${route}&schemaVersion=${schemaVersion}`;
  },
  getPublicRoute: (
    clientId: string,
    schemaKey: string,
    schemaVersion?: string,
  ) =>
    `${PUBLIC_URI_PREFIX}${GetDDMSchema.getRoute(
      clientId,
      schemaKey,
      schemaVersion,
    )}`,
  method: Method.GET,
  metricsPublisher:
    metricsPublisher.newChildActionPublisherForMethod('GetDDMSchema'),
  numberOfRetries: MAX_RETRY_ATTEMPT,
  keepUnusedDataFor: DATA_STORED_IN_API_CACHE_SECS,
};
